/**
 * Helpers for the *positional* tracking of confirmed `/` slash-command picks
 * inside the input textarea. After a pick the textarea content contains *only*
 * the entity name (the slash is dropped), so we can no longer recover token
 * boundaries by scanning for `/EntityName`. Instead we keep an explicit
 * `LookupInputRange[]` alongside the textarea value:
 *
 *  - `LookupHighlightLayer` paints the highlight pill backdrop using these
 *    ranges.
 *  - The input keydown handlers use `findRangeEndingAtCaret` to support atomic
 *    Backspace (single keystroke removes the entire entity range).
 *  - `rebaseRangesAfterEdit` keeps ranges aligned as the user types/edits
 *    surrounding text; any edit that *overlaps* a range invalidates it (the
 *    user has typed inside the entity name, so it's no longer a confirmed
 *    pick).
 */

import type { LookupItem } from '../AskAsperAgentic.types';

export interface LookupInputRange {
    lookup: LookupItem;
    /** Inclusive index of the entity name's first character in the value. */
    start: number;
    /** Exclusive index just past the entity name's last character. */
    end: number;
}

/**
 * Find the smallest contiguous span where `oldValue` differs from `newValue`.
 * Returns `{ prefix, oldEnd, newEnd }` describing the change region:
 *   - `[0, prefix)` is identical in both strings (common prefix)
 *   - `oldValue[prefix..oldEnd]` was replaced with `newValue[prefix..newEnd]`
 *   - `oldValue[oldEnd..]` equals `newValue[newEnd..]` (common suffix)
 *
 * Used to figure out which existing ranges need shifting vs invalidating.
 */
function computeEditSpan(
    oldValue: string,
    newValue: string,
): { prefix: number; oldEnd: number; newEnd: number } {
    if (oldValue === newValue) {
        return { prefix: oldValue.length, oldEnd: oldValue.length, newEnd: newValue.length };
    }
    const oldLen = oldValue.length;
    const newLen = newValue.length;
    let prefix = 0;
    const maxPrefix = Math.min(oldLen, newLen);
    while (prefix < maxPrefix && oldValue[prefix] === newValue[prefix]) prefix++;

    let suffix = 0;
    const maxSuffix = Math.min(oldLen - prefix, newLen - prefix);
    while (
        suffix < maxSuffix &&
        oldValue[oldLen - 1 - suffix] === newValue[newLen - 1 - suffix]
    ) {
        suffix++;
    }
    return { prefix, oldEnd: oldLen - suffix, newEnd: newLen - suffix };
}

/**
 * Adjust ranges after a textarea edit:
 *   - Range entirely before the edit → unchanged.
 *   - Range entirely after the edit → shifted by the length delta.
 *   - Range overlapping the edit → invalidated (user typed inside an entity,
 *     so it's no longer a confirmed pick). The caller should also drop the
 *     associated lookup from its `selectedLookups` mirror.
 *
 * When the value is unchanged this is a no-op (returns the same array).
 */
export function rebaseRangesAfterEdit(
    oldValue: string,
    newValue: string,
    ranges: LookupInputRange[],
): LookupInputRange[] {
    if (ranges.length === 0 || oldValue === newValue) return ranges;
    const { prefix, oldEnd, newEnd } = computeEditSpan(oldValue, newValue);
    const delta = newEnd - oldEnd;
    const rebased: LookupInputRange[] = [];
    for (const r of ranges) {
        if (r.end <= prefix) {
            rebased.push(r);
            continue;
        }
        if (r.start >= oldEnd) {
            rebased.push({ ...r, start: r.start + delta, end: r.end + delta });
            continue;
        }
        // Range overlaps the edit → drop it.
    }
    return rebased;
}

/**
 * Return the range whose `end` coincides with `caretPos`, or null. Used by the
 * Backspace handler to atomically delete a confirmed entity in one keystroke.
 */
export function findRangeEndingAtCaret(
    caretPos: number,
    ranges: LookupInputRange[],
): LookupInputRange | null {
    if (caretPos <= 0 || ranges.length === 0) return null;
    return ranges.find((r) => r.end === caretPos) ?? null;
}

/**
 * Project `ranges` to a deduplicated list of `LookupItem`s in left-to-right
 * order. Hosts that only care about *which* items are present (not their
 * positions) consume this shape via `onLookupsChange`.
 */
export function rangesToLookups(ranges: LookupInputRange[]): LookupItem[] {
    if (ranges.length === 0) return [];
    const seen = new Set<string>();
    const out: LookupItem[] = [];
    for (const r of ranges) {
        const key = `${r.lookup.column_name}::${r.lookup.entity_name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(r.lookup);
    }
    return out;
}
